import React from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Alert } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { FormField, getFieldError } from '@/components/form/FormField';
import {
  useLicense,
  useCreateLicense,
  useUpdateLicense,
} from '../services/licenseService';
import { useContracts } from '../services/contractService';
import { LicenseType } from '../types';
import { parseCalendarDate, toCalendarIso } from '../utils/calendarDate';
import type { CreateLicense, UpdateLicense } from '../types';

/**
 * License form validation schema
 *
 * Defines validation rules for all license fields using Zod.
 * Ensures type safety and runtime validation.
 */
const licenseSchema = z.object({
  contractId: z.string().min(1, 'Contract is required'),
  productName: z.string().min(1, 'Product name is required').max(200, 'Product name is too long'),
  version: z.string().max(50, 'Version is too long').optional(),
  licenseType: z.nativeEnum(LicenseType),
  totalQuantity: z.number().int().min(1, 'Total quantity must be at least 1'),
  costPerUnit: z.number().min(0, 'Cost per unit must be positive'),
  expirationDate: z.date().optional().nullable(),
  licenseKey: z.string().max(500, 'License key is too long').optional(),
  notes: z.string().max(2000, 'Notes is too long').optional(),
  isActive: z.boolean(),
}) satisfies z.ZodType<
  Omit<CreateLicense, 'expirationDate'> & {
    expirationDate?: Date | null;
  }
>;


/**
 * License Form Page Component
 *
 * Provides a comprehensive form for creating or editing licenses with
 * complete validation, error handling, and accessibility features.
 */
const LicenseForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams({ strict: false }) as { id?: string };
  const isEditMode = !!id;

  const { data: license, isLoading: isLoadingLicense } = useLicense(id);
  const { data: contractsData, isLoading: isLoadingContracts } = useContracts({
    pageSize: 1000,
  });
  const createMutation = useCreateLicense();
  const updateMutation = useUpdateLicense();

  const [submitError, setSubmitError] = React.useState<string | null>(null);

  /**
   * Initialize form with TanStack Form and Zod validation
   */
  const form = useForm({
    defaultValues: {
      contractId: license?.contractId || '',
      productName: license?.productName || '',
      version: license?.version || '',
      licenseType: license?.licenseType || LicenseType.PerUser,
      totalQuantity: license?.totalQuantity || 1,
      costPerUnit: license?.costPerUnit || 0,
      expirationDate: parseCalendarDate(license?.expirationDate), // calendar day (#65)
      licenseKey: license?.licenseKey || '',
      notes: license?.notes || '',
      isActive: license?.isActive ?? true,
    },
    onSubmit: async ({ value }) => {
      try {
        setSubmitError(null);

        // Convert date to ISO string for API
        const payload = {
          ...value,
          expirationDate: value.expirationDate
            ? toCalendarIso(value.expirationDate)
            : undefined,
          version: value.version || undefined,
          licenseKey: value.licenseKey || undefined,
          notes: value.notes || undefined,
        };

        if (isEditMode && id) {
          const updated = await updateMutation.mutateAsync({
            id,
            data: payload as UpdateLicense,
          });
          navigate({ to: `/licenses/${updated.id}` });
        } else {
          const created = await createMutation.mutateAsync(payload as CreateLicense);
          navigate({ to: `/licenses/${created.id}` });
        }
      } catch (error) {
        console.error('Failed to save license:', error);
        setSubmitError('Failed to save license. Please try again.');
      }
    },
  });

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    if (isEditMode && id) {
      navigate({ to: `/licenses/${id}` });
    } else {
      navigate({ to: '/licenses' });
    }
  };

  // Loading state
  if ((isEditMode && isLoadingLicense) || isLoadingContracts) {
    return (
      <div className="max-w-3xl mx-auto py-8 px-4">
        <div className="flex justify-center items-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  // Error state - license not found in edit mode
  if (isEditMode && !license) {
    return (
      <div className="max-w-3xl mx-auto py-8 px-4">
        <Alert variant="destructive">License not found.</Alert>
        <div className="mt-4">
          <Button variant="ghost" onClick={handleBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Licenses
          </Button>
        </div>
      </div>
    );
  }

  const contracts = contractsData?.items || [];

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      {/* Page Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={handleBack} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <h1 className="text-3xl font-semibold text-grey-900">
          {isEditMode ? 'Edit License' : 'Create New License'}
        </h1>
      </div>

      {/* Form */}
      <Card className="p-6">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <div className="grid grid-cols-12 gap-6">
            {/* Contract Selection */}
            <div className="col-span-12">
              <form.Field
                name="contractId"
                validators={{
                  onChange: licenseSchema.shape.contractId,
                }}
              >
                {(field) => (
                  <FormField label="Contract" required error={getFieldError(field.state.meta.errors)}>
                    <Select value={field.state.value} onValueChange={field.handleChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a contract" />
                      </SelectTrigger>
                      <SelectContent>
                        {contracts.map((contract) => (
                          <SelectItem key={contract.id} value={contract.id}>
                            {contract.title} - {contract.contractNumber}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                )}
              </form.Field>
            </div>

            {/* Product Name */}
            <div className="col-span-12 md:col-span-8">
              <form.Field
                name="productName"
                validators={{
                  onChange: licenseSchema.shape.productName,
                }}
              >
                {(field) => (
                  <FormField label="Product Name" required error={getFieldError(field.state.meta.errors)}>
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      placeholder="e.g., Microsoft Office 365 E5"
                    />
                  </FormField>
                )}
              </form.Field>
            </div>

            {/* Version */}
            <div className="col-span-12 md:col-span-4">
              <form.Field name="version">
                {(field) => (
                  <FormField label="Version" error={getFieldError(field.state.meta.errors)}>
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      placeholder="e.g., 2024"
                    />
                  </FormField>
                )}
              </form.Field>
            </div>

            {/* License Type */}
            <div className="col-span-12 md:col-span-6">
              <form.Field
                name="licenseType"
                validators={{
                  onChange: licenseSchema.shape.licenseType,
                }}
              >
                {(field) => (
                  <FormField label="License Type" required error={getFieldError(field.state.meta.errors)}>
                    <Select value={field.state.value} onValueChange={(val) => field.handleChange(val as LicenseType)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select license type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={LicenseType.PerUser}>Per User</SelectItem>
                        <SelectItem value={LicenseType.PerSeat}>Per Seat</SelectItem>
                        <SelectItem value={LicenseType.Concurrent}>Concurrent</SelectItem>
                        <SelectItem value={LicenseType.Site}>Site License</SelectItem>
                        <SelectItem value={LicenseType.Enterprise}>Enterprise</SelectItem>
                        <SelectItem value={LicenseType.ConsumptionBased}>Consumption Based</SelectItem>
                        <SelectItem value={LicenseType.NamedUser}>Named User</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormField>
                )}
              </form.Field>
            </div>

            {/* Total Quantity */}
            <div className="col-span-12 md:col-span-6">
              <form.Field
                name="totalQuantity"
                validators={{
                  onChange: licenseSchema.shape.totalQuantity,
                }}
              >
                {(field) => (
                  <FormField label="Total Quantity" required error={getFieldError(field.state.meta.errors)}>
                    <Input
                      type="number"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(parseInt(e.target.value, 10) || 0)}
                      onBlur={field.handleBlur}
                      min={1}
                      step={1}
                    />
                  </FormField>
                )}
              </form.Field>
            </div>

            {/* Cost Per Unit */}
            <div className="col-span-12 md:col-span-6">
              <form.Field
                name="costPerUnit"
                validators={{
                  onChange: licenseSchema.shape.costPerUnit,
                }}
              >
                {(field) => (
                  <FormField label="Cost Per Unit" required error={getFieldError(field.state.meta.errors)}>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-grey-500 text-sm">$</span>
                      <Input
                        className="pl-7"
                        type="number"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(parseFloat(e.target.value) || 0)}
                        onBlur={field.handleBlur}
                        min={0}
                        step={0.01}
                        placeholder="0.00"
                      />
                    </div>
                  </FormField>
                )}
              </form.Field>
            </div>

            {/* Expiration Date */}
            <div className="col-span-12 md:col-span-6">
              <form.Field name="expirationDate">
                {(field) => (
                  <FormField label="Expiration Date" error={getFieldError(field.state.meta.errors)}>
                    <DatePicker
                      value={field.state.value}
                      onChange={(date) => field.handleChange(date ?? null)}
                    />
                  </FormField>
                )}
              </form.Field>
            </div>

            {/* License Key */}
            <div className="col-span-12">
              <form.Field name="licenseKey">
                {(field) => (
                  <FormField label="License Key" error={getFieldError(field.state.meta.errors)}>
                    <Input
                      className="font-mono"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      placeholder="Enter license key or activation code"
                    />
                  </FormField>
                )}
              </form.Field>
            </div>

            {/* Notes */}
            <div className="col-span-12">
              <form.Field name="notes">
                {(field) => (
                  <FormField label="Notes" error={getFieldError(field.state.meta.errors)}>
                    <Textarea
                      rows={4}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      placeholder="Additional notes or instructions..."
                    />
                  </FormField>
                )}
              </form.Field>
            </div>

            {/* Is Active */}
            <div className="col-span-12">
              <form.Field name="isActive">
                {(field) => (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={field.state.value}
                      onCheckedChange={(checked) => field.handleChange(checked === true)}
                      id="isActive"
                    />
                    <Label htmlFor="isActive">Active License</Label>
                  </div>
                )}
              </form.Field>
            </div>

            {/* Submit Error */}
            {submitError && (
              <div className="col-span-12">
                <Alert variant="destructive">{submitError}</Alert>
              </div>
            )}

            {/* Form Actions */}
            <div className="col-span-12">
              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" size="lg" onClick={handleBack}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="lg"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  <Save className="mr-2 h-4 w-4" />
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : isEditMode
                    ? 'Save Changes'
                    : 'Create License'}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default LicenseForm;
